import { Injectable, OnDestroy } from '@angular/core';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { Namespace } from 'enums-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { BullhornSettingsService } from 'settings-lib';
import { IdentityStorage } from 'storage-service-lib';
import { EntityTypeService, IEntityLabelPair } from '../entity-type/entity-type.service';

@Injectable({ providedIn: 'root' })
export class BullhornEntityFieldMetaService implements OnDestroy {
  private entityMetaSubject = new BehaviorSubject<IEntityLabelPair[]>([]);
  private readonly namespace: Namespace;
  private destroy$ = new Subject<void>();
  loading$ = new BehaviorSubject<boolean>(true);
  entityMeta$ = this.entityMetaSubject.asObservable();

  private clientLeadOpportunityEnabled = false;

  private _completeEntityFieldsMap = [
    { entityName: BullhornEntityTypes.Candidate, fields: ['id'] },
    { entityName: BullhornEntityTypes.ClientCorporation, fields: ['id'] },
    {
      entityName: BullhornEntityTypes.ClientContact,
      fields: ['clientCorporation'],
    },
    {
      entityName: BullhornEntityTypes.JobOrder,
      fields: ['clientContact', 'clientCorporation', 'opportunity'],
    },
    {
      entityName: BullhornEntityTypes.Placement,
      fields: ['candidate', 'clientCorporation', 'clientContact', 'vendorClientCorporation', 'jobOrder'],
    },
    {
      entityName: BullhornEntityTypes.JobSubmission,
      fields: ['candidate', 'jobOrder'],
    },
    {
      entityName: BullhornEntityTypes.Opportunity,
      fields: ['clientContact', 'clientCorporation'],
    },
  ];

  constructor(
    private identityStorage: IdentityStorage,
    private entityTypeService: EntityTypeService,
    private messageNotificationService: MessageNotificationService,
    private bullhornSettingsService: BullhornSettingsService
  ) {
    this.namespace = this.identityStorage.get().namespace;
    this.initializeEntityMeta();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeEntityMeta(): void {
    this.loading$.next(true);
    this.bullhornSettingsService.clientLeadOpportunityEnabled
      .pipe(
        switchMap((enabled) => {
          this.clientLeadOpportunityEnabled = enabled;
          return this.getEntityMeta();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (meta) => {
          this.entityMetaSubject.next(meta);
          this.loading$.next(false);
        },
        error: (error) => {
          this.messageNotificationService.error('Failed to initialize entity field metadata.', error);
          this.loading$.next(false);
        }
      });
  }

  get isClientLeadOpportunityEnabled(): boolean {
    return this.clientLeadOpportunityEnabled;
  }

  getEntityMeta(): Observable<IEntityLabelPair[]> {
    const entityFields = this.getEntityFieldsForMeta();
    return forkJoin(
      entityFields.map((entityField) => {
        return this.entityTypeService.getEntityFieldsForNamespace(this.namespace, entityField.entityName, entityField.fields).pipe(
          catchError((error) => {
            this.messageNotificationService.error(`Failed to load entity field metadata for ${entityField.entityName}.`, error);
            return of(null);
          })
        ) as Observable<IEntityLabelPair | null>;
      })
    ).pipe(
      map((results) => results.filter((result): result is IEntityLabelPair => result !== null))
    );
  }

  getMetaLabelFromPath(entities: IEntityLabelPair[], path: string): string | undefined {
    if (!entities || entities.length === 0) {
      return undefined;
    }

    if (path.split('.').length > 2) {
      path = path.split('.').slice(-2).join('.');
    }

    const fieldMeta = this.getFieldMetaFromEntities(entities);

    return (
      entities.find((item) => item.objectName.toLowerCase() === path.toLowerCase())?.label ??
      fieldMeta.find((field) => field.path.toLowerCase() === path.toLowerCase())?.label
    );
  }

  private getEntityFieldsForMeta(): { entityName: string; fields: string; }[] {
    return this.getAvailableEntityFieldsMap().map((entityFieldMap) => ({
      ...entityFieldMap,
      fields: entityFieldMap.fields.join(','),
    }));
  }

  private getAvailableEntityFieldsMap() {
    return this._completeEntityFieldsMap
      .filter((entityFieldsMap) => this.clientLeadOpportunityEnabled || entityFieldsMap.entityName !== 'Opportunity')
      .map((entityFieldsMap) => this.removeOpportunityFields(entityFieldsMap));
  }

  private removeOpportunityFields(entityFieldMap: { entityName: BullhornEntityTypes; fields: string[] }) {
    // If opportunity entities are enabled, then we don't need to filter out opportunity fields
    if (this.clientLeadOpportunityEnabled || !(entityFieldMap.entityName === BullhornEntityTypes.JobOrder)) {
      return entityFieldMap;
    }
    return {
      ...entityFieldMap,
      fields: entityFieldMap.fields.filter((f) => !f.includes('opportunity'))
    };
  }

  private getFieldMetaFromEntities(entities: IEntityLabelPair[]): any[] {
    return entities.flatMap(entity => entity.fields || []);
  }
}
